import { isEqual } from 'lodash';

import {
  AdminUpdateOrderRequestDTO,
  UpdateOrderItemsSection,
  UpdateOrderDeliverySection,
} from '../types/dtos/admin-update-order.dto';
import { OrderFormState } from './order-to-form-state.util';
import { DeliveryAddress, DeliveryDay, DeliveryType } from '../types/delivery-selection.type';
import { OrderItem } from '../types/order-item.type';
import { toApiDeliveryType } from '../constants/delivery-type.constants';

function toAddressPayload(addr: DeliveryAddress) {
  return {
    address: addr.address,
    zipCode: addr.zipCode,
    city: addr.city,
    ...(addr.title ? { title: addr.title } : {}),
    ...(addr.complement ? { complement: addr.complement } : {}),
    ...(addr.comment ? { comment: addr.comment } : {}),
  };
}

function formatItemsForPayload(items: OrderItem[]): UpdateOrderItemsSection {
  return {
    products: items
      .filter((item) => item.type !== 'basket' && item.type !== 'virtual')
      .map((item) => ({
        id: item.productId ?? '',
        type: 'product' as const,
        quantity: item.quantity,
      })),
    baskets: items
      .filter((item) => item.type === 'basket')
      .map((item) => ({
        id: item.productId ?? '',
        type: 'basket' as const,
        quantity: item.quantity,
      })),
    virtuals: items
      .filter((item) => item.type === 'virtual')
      .map((item) => ({
        type: 'virtual' as const,
        name: item.name,
        quantity: item.quantity,
        price: item.price,
        tvaRate: item.tvaRate,
        size: item.size,
        noLabel: item.noLabel,
        valueZero: item.valueZero,
      })),
  };
}

function formatDeliveryForPayload(
  deliveryType: DeliveryType,
  deliveryAddress: DeliveryAddress | null,
  billingAddress: DeliveryAddress | null,
  deliveryDay: DeliveryDay,
  deliveryPointId: string | null,
  organizationId: string | null
): UpdateOrderDeliverySection {
  const apiType = toApiDeliveryType(deliveryType);
  const deliveryDate = deliveryDay.date.format('YYYY-MM-DD');
  const deliverySchedule = deliveryDay.schedule;

  switch (apiType) {
    case 'home': {
      if (!deliveryAddress) {
        throw new Error('Home delivery requires a deliveryAddress');
      }
      const billing = billingAddress ?? deliveryAddress;
      return {
        deliveryType: 'home',
        deliveryDate,
        deliverySchedule,
        deliveryInfos: {
          deliveryId: null,
          deliveryType: 'home',
          deliveryAddress: toAddressPayload(deliveryAddress),
          deliveryBillingAddress: toAddressPayload(billing),
          deliveryDate,
          deliverySchedule,
        },
      };
    }
    case 'relay':
      if (!deliveryAddress) {
        throw new Error('Relay delivery requires a deliveryAddress');
      }
      return {
        deliveryType: 'relay',
        deliveryDate,
        deliverySchedule,
        relayId: deliveryAddress._id,
        ...(billingAddress ? { billingAddress: toAddressPayload(billingAddress) } : {}),
      };
    case 'drive':
      return {
        deliveryType: 'drive',
        deliveryDate,
        deliverySchedule,
        ...(billingAddress ? { billingAddress: toAddressPayload(billingAddress) } : {}),
      };
    case 'organization':
      if (!deliveryAddress) {
        throw new Error('Organization delivery requires a deliveryAddress');
      }
      if (!deliveryPointId) {
        throw new Error('Organization delivery requires a deliveryPointId');
      }
      if (!organizationId) {
        throw new Error('Organization delivery requires an organizationId');
      }
      return {
        deliveryType: 'organization',
        deliveryDate,
        deliverySchedule,
        organizationId,
        deliveryPointId,
        ...(billingAddress ? { billingAddress: toAddressPayload(billingAddress) } : {}),
      };
  }
}

function momentDayEquals(a: DeliveryDay | null, b: DeliveryDay | null): boolean {
  if (a === null && b === null) return true;
  if (a === null || b === null) return false;
  return a.formattedDate === b.formattedDate && a.schedule === b.schedule;
}

export function buildUpdateOrderPayload(
  initial: OrderFormState,
  current: OrderFormState
): AdminUpdateOrderRequestDTO | null {
  const payload: AdminUpdateOrderRequestDTO = {};

  if (current.status !== initial.status) payload.status = current.status;
  if (current.advStatus !== initial.advStatus) payload.advStatus = current.advStatus;
  if (current.deliveryCost !== initial.deliveryCost) payload.deliveryCost = current.deliveryCost;

  if (!isEqual(current.items, initial.items)) {
    payload.items = formatItemsForPayload(current.items);
  }

  const deliveryChanged =
    current.deliveryType !== initial.deliveryType ||
    !isEqual(current.deliveryAddress, initial.deliveryAddress) ||
    !isEqual(current.billingAddress, initial.billingAddress) ||
    !momentDayEquals(current.deliveryDay, initial.deliveryDay) ||
    current.deliveryPointId !== initial.deliveryPointId ||
    current.organizationId !== initial.organizationId;

  if (deliveryChanged && current.deliveryDay) {
    payload.delivery = formatDeliveryForPayload(
      current.deliveryType,
      current.deliveryAddress,
      current.billingAddress,
      current.deliveryDay,
      current.deliveryPointId,
      current.organizationId
    );
  }

  if (Object.keys(payload).length === 0) return null;
  return payload;
}
